// src/components/notifications/NotificationPreferences.jsx
import React, { useState, useCallback, useRef } from "react";
import { useUpdateNotificationPreferences } from "../../hooks/notifications/useNotificationPreferences";
import useToast from "../../hooks/useToast";

/**
 * NotificationPreferences Component
 *
 * Simple preferences component using React Query
 */
const NotificationPreferences = ({
  preferences = {
    follow: true,
    "message.liked": true,
    "message.replied": true,
  },
  onSave,
  showSaveButton = false,
  autoSave = true,
  className = "",
}) => {
  const [localPreferences, setLocalPreferences] = useState(preferences);
  const [isUpdating, setIsUpdating] = useState(false);
  const lastRequestRef = useRef(null);
  const lastToastRef = useRef(null);
  const toast = useToast();

  const updatePreferencesMutation = useUpdateNotificationPreferences();

  // Update local preferences when props change (when API data loads)
  React.useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  // Handle preference toggle with duplicate prevention
  const handlePreferenceChange = useCallback(
    async (preferenceType, value) => {
      // Prevent multiple simultaneous calls
      if (isUpdating || updatePreferencesMutation.isLoading) {
        return;
      }

      const newPreferences = {
        ...localPreferences,
        [preferenceType]: value,
      };

      // Create a unique request signature to prevent duplicates
      const requestSignature = JSON.stringify(newPreferences);

      // Check if this exact request was just made (within last 1 second)
      const now = Date.now();
      if (
        lastRequestRef.current &&
        lastRequestRef.current.signature === requestSignature &&
        now - lastRequestRef.current.timestamp < 1000
      ) {
        console.log("🔔 Duplicate request prevented:", requestSignature);
        return;
      }

      // Update local state immediately for responsive UI
      setLocalPreferences(newPreferences);

      if (autoSave) {
        try {
          setIsUpdating(true);

          // Record this request
          lastRequestRef.current = {
            signature: requestSignature,
            timestamp: now,
          };

          const result = await updatePreferencesMutation.mutateAsync(
            newPreferences
          );

          // Only show success toast once per unique request
          const toastSignature = `success_${requestSignature}`;
          if (
            result &&
            result.success &&
            (!lastToastRef.current ||
              lastToastRef.current !== toastSignature ||
              now - (lastRequestRef.current?.timestamp || 0) > 500)
          ) {
            toast.success("Notification preferences updated successfully");
            lastToastRef.current = toastSignature;
          }

          if (onSave) {
            onSave(newPreferences);
          }
        } catch (error) {
          // Only show error toast once per unique request
          const errorToastSignature = `error_${requestSignature}`;
          if (
            !lastToastRef.current ||
            lastToastRef.current !== errorToastSignature ||
            now - (lastRequestRef.current?.timestamp || 0) > 500
          ) {
            toast.error("Failed to update notification preferences");
            lastToastRef.current = errorToastSignature;
          }

          // Rollback on error
          setLocalPreferences(preferences);

          // Clear the request record on error
          lastRequestRef.current = null;
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [
      localPreferences,
      isUpdating,
      autoSave,
      updatePreferencesMutation,
      toast,
      onSave,
      preferences,
    ]
  );

  // Preference toggle component
  const PreferenceToggle = ({ type, label, description, disabled = false }) => {
    const isEnabled = localPreferences[type] || false;
    const toggleId = `notification-${type}`;

    return (
      <div className="flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0">
        <div className="flex-1 pr-4">
          <label
            htmlFor={toggleId}
            className="block text-sm font-medium text-gray-900 cursor-pointer"
          >
            {label}
          </label>
          {description && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
        </div>

        <div className="flex items-center">
          <button
            id={toggleId}
            type="button"
            disabled={
              disabled || updatePreferencesMutation.isLoading || isUpdating
            }
            onClick={() => handlePreferenceChange(type, !isEnabled)}
            className={`
              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${isEnabled ? "bg-blue-600" : "bg-gray-200"}
              ${
                disabled || updatePreferencesMutation.isLoading || isUpdating
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }
            `}
            role="switch"
            aria-checked={isEnabled}
            aria-labelledby={toggleId}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                transition duration-200 ease-in-out
                ${isEnabled ? "translate-x-5" : "translate-x-0"}
              `}
            />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`notification-preferences ${className}`}>
      <div className="space-y-6">
        {/* Social Notifications */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Social Activity
          </h3>
          <div className="space-y-0 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2">
              <PreferenceToggle
                type="follow"
                label="Follow Notifications"
                description="Get notified when someone follows you"
                disabled={updatePreferencesMutation.isLoading}
              />
            </div>
          </div>
        </div>

        {/* Thread Notifications */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Thread Activity
          </h3>
          <div className="space-y-0 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2">
              <PreferenceToggle
                type="message.liked"
                label="Message Likes"
                description="Get notified when someone likes your message in a thread"
                disabled={updatePreferencesMutation.isLoading}
              />
            </div>
            <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
              <PreferenceToggle
                type="message.replied"
                label="Message Replies"
                description="Get notified when someone replies to your message in a thread"
                disabled={updatePreferencesMutation.isLoading}
              />
            </div>
          </div>
        </div>

        {/* Add more preference sections here as needed */}
      </div>

      {/* Manual save button (if not auto-save) */}
      {showSaveButton && !autoSave && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() =>
              handlePreferenceChange("follow", localPreferences.follow)
            }
            disabled={updatePreferencesMutation.isLoading}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updatePreferencesMutation.isLoading
              ? "Saving..."
              : "Save Preferences"}
          </button>
        </div>
      )}

      {/* Error message */}
      {updatePreferencesMutation.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">
            Failed to save preferences:{" "}
            {updatePreferencesMutation.error.message}
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;
